import React, { useState, useContext, useRef, useEffect } from 'react'
import { DAGTask } from '@pollination-solutions/pollination-sdk'
import { RootContext as context } from '~/state/RootContext'
import { TaskNodeDetail } from './TaskNodeDetail'
import { TaskNodeParameter } from './TaskNodeParameter'
import { Adjustments, Folder, Refresh } from '~/components/icons'

interface TaskNodeProps {
  position: [number, number]
  task: DAGTask
}

export const TaskNode = ({ position, task }: TaskNodeProps): JSX.Element => {
  const [x, y] = position

  const { meta, graph, layout, register, highlight, setFlow } = useContext(context)

  const z = layout?.zoom ?? 1

  const [dx, dy] = layout?.position ?? [0, 0]
  const [cx, cy] = [x + dx, y + dy]

  const [isExpanded, setIsExpanded] = useState(false)
  const [isIdle, setIsIdle] = useState(true)

  const nodeRef = useRef<HTMLDivElement>(null)

  // Register node after draw
  useEffect(() => {
    const isRegistered = graph?.nodes?.find((n) => n.key.includes(task.name))

    if (isRegistered === undefined) {
      const dependencies = [
        ...(task.needs ?? []),
        // @ts-ignore
        ...(task.arguments?.filter((p) => p.from.type.includes('Input')).map((p) => `input-${p.from.variable}`)) as any[],
        ...(task.loop
          ? [
              (task.loop.from as any).type === 'tasks'
                ? `${(task.loop.from as any).name}`
                : `input-${(task.loop.from as any).variable}`,
            ]
          : []),
      ]
      register(task.name, nodeRef, dependencies)
    }
  }, [task, graph, register])

  // Modify node opacity based on highlight
  const isFaded = (graph?.highlight?.length ?? 0) > 0 && !graph?.highlight.includes(task.name)

  const iconProps = { mode: 'dark' as 'dark' | 'light', filled: false }

  const template = task.template.split('/')

  const isRecipe = template.length === 1

  const templateFields = isRecipe
    ? [{ label: 'recipe', value: template[0] }]
    : [
        { label: 'plugin', value: template[0] },
        {
          label: 'function',
          value: meta?.domain ? (
            <a className="task-link" href={`/plugins/${meta.owner}/${template[0]}?tab=functions#${template[1]}`}>
              {template[1]}
              <style jsx>{`
                .task-link:link {
                  color: '#555';
                }

                .task-link:visited {
                  color: '#555';
                }

                .task-link:hover {
                  color: '#333';
                }

                .task-link:active {
                  color: '#555';
                }
              `}</style>
            </a>
          ) : (
            template[1]
          ),
        },
      ]

  return (
    <div
      className={`task-node-container ${isRecipe ? 'task-node-container--recipe' : ''} ${
        isExpanded ? 'task-node-container--expanded' : ''
      } ${isIdle ? 'task-node-container--idle' : ''}`}
      style={{
        position: 'absolute',
        left: (cx - 150) * z,
        top: (cy - 20) * z,
        opacity: isFaded ? 0.5 : 1,
        transform: `scale(${layout?.zoom ?? 1})`,
      }}
      onPointerEnter={() => highlight(task.name)}
      onPointerLeave={() => {
        setIsIdle(true)
        highlight(undefined)
      }}
    >
      <div
        className={`${isRecipe ? 'recipe-node' : 'task-node'}`}
        ref={nodeRef}
        onClick={
          isRecipe
            ? () => setFlow(task.template)
            : () => {
                setIsIdle(false)
                setIsExpanded((state) => !state)
              }
        }
      >
        {isRecipe ? (
          <>
            <div className="recipe-node-icon" />
            <div className="task-node-label">
              <div className="task-node-label-name">{task.name}</div>
            </div>
            {task.loop ? <Refresh mode="dark" filled={false} /> : null}
          </>
        ) : (
          <>
            <div className="task-node-icon">
              <div className="task-node-icon-image" />
            </div>
            <div className="task-node-label">
              <div className="task-node-label-name">{task.name}</div>
            </div>
            {task.loop ? <Refresh mode="dark" filled={false} /> : null}
          </>
        )}
      </div>
      {isIdle && !isExpanded ? (
        <button
          className="task-node-expand"
          onClick={() => {
            setIsIdle(false)
            setIsExpanded((state) => !state)
          }}
        >
          <svg width={15} height={15} viewBox="-1 1 12 12">
            <polyline
              points="0,5 5,10 10,5"
              stroke="#333"
              strokeWidth="2px"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </button>
      ) : null}
      <div className="task-node-details">
        <TaskNodeDetail
          label="template"
          value={task.template.split('/')[1] ?? task.template}
          icon={<Adjustments {...iconProps} />}
          fields={[{ label: 'owner', value: meta?.owner ?? '' }, ...templateFields]}
        />
        {task.sub_folder ? (
          <TaskNodeDetail label="subfolder" value={task.sub_folder} icon={<Folder {...iconProps} />} />
        ) : null}
        {task.loop ? (
          <TaskNodeDetail label="loop" value={(task.loop.from as any).variable} icon={<Refresh {...iconProps} />} />
        ) : null}
        <div className="task-node-io">
          <div className="task-node-io-col">
            {task.arguments?.map((p) => (
              <TaskNodeParameter
                key={`task-${p.name}-input`}
                mode="input"
                parameter={p}
              />
            ))}
            {task.returns?.map((p) => {
              return (
                <TaskNodeParameter
                  key={`task-${name}-output-parameter-${p.name}`}
                  mode="output"
                  parameter={p}
                />
              )
            })}
          </div>
        </div>
      </div>
      <style jsx>{`
        .task-node-container {
          width: 300px;
          max-height: 36px;
          box-sizing: content-box;
          background: #fcfcfc;
          overflow: hidden;
          border-radius: 18px;
          border-top-right-radius: 18px;
          border-top-left-radius: 18px;
          border: 2px solid #333;
          border-top: none;
          transition: max-height, opacity;
          transition-duration: 300ms;
          transition-timing-function: ease;
          z-index: 2;
        }

        .task-node-container--recipe {
          max-height: 38px;
          border-radius: 6px;
          border: none;
        }

        .task-node-container--idle:hover:not(.task-node-container--expanded):not(.task-node-container--recipe) {
          max-height: 59px;
        }

        .task-node-container--expanded {
          max-height: 800px;
          z-index: 3;
        }

        .task-node-expand {
          margin-top: 3px;
          margin-bottom: 3px;
          width: 100%;
          display: flex;
          justify-content: center;
          overflow: hidden;
          border: none;
          background: none;
        }

        .task-node-expand:hover {
          cursor: pointer;
        }

        .task-node {
          width: 290px;
          height: 34px;
          box-sizing: content-box;
          padding-right: 10px;
          border-radius: 34px;
          border: 2px solid #333;
          background: #fff;
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          overflow: hidden;
          user-select: none;
          transform: translateX(-2px);
        }

        .task-node:hover {
          cursor: pointer;
        }

        .task-node-icon {
          min-width: 34px;
          min-height: 34px;
          box-sizing: content-box;
          margin-right: 8px;
          border-radius: 34px;
          border: 2px solid #333;
          background: #badaf2;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: translate(-2px);
        }

        .task-node-icon-image {
          width: 18px;
          height: 18px;
          border-radius: 18px;
          background: #fff;
        }

        .recipe-node {
          width: 300px;
          height: 38px;
          box-sizing: border-box;
          padding-left: 8px;
          padding-right: 8px;
          border-radius: 6px;
          border: 2px solid #333;
          background: #fff;
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          overflow: hidden;
          user-select: none;
        }

        .recipe-node:hover {
          cursor: pointer;
        }

        .recipe-node-icon {
          width: 18px;
          height: 18px;
          border-radius: 18px;
          background: #badaf2;
          margin-right: 6px;
        }

        .task-node-label {
          flex-grow: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .task-node-label-name {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
        }

        .task-node-label-template {
          font-family: 'Space Mono', monospace;
          font-size: 8px;
        }

        .task-node-details {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          padding: 9px;
          padding-top: 0px;
        }

        .task-node-io {
          width: 100%;
          max-width: 100%;
          display: grid;
          grid-template-columns: 1fr;
        }

        .task-node-io-col {
          width: 100%;
          display: flex;
          flex-direction: column;
        }
      `}</style>
    </div>
  )
}
